import React, { useEffect, useState } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import { useAuthUser } from "react-auth-kit"
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { useQuery } from "react-query"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import Snackbar from "@mui/material/Snackbar"
import MuiAlert from "@mui/material/Alert"
import api from "../../../../utils/api"

interface Voter {
  id: string
  name: string
  username: string
  joined?: boolean
  voted?: boolean
}

interface Question {
  id: string
  description: string
  questionType: string
}

interface Answer {
  id: string
  questionId: string
  option: string
  optionNum: number
}

interface InstanceInfo {
  description: string
  votingType: string
  questionCount: number
  status: string
}

const AdminButton: React.FC<{
  label: string
  pathSegment: string
}> = ({ label, pathSegment }) => {
  const { id } = useParams<{ id: string }>()
  return (
    <Button
      variant="contained"
      color="primary"
      component={Link}
      to={`/admin/event-admin/instance/${id}/${pathSegment}`}
      style={{ marginTop: "10px" }}
    >
      {label}
    </Button>
  )
}

const VoterTable: React.FC<{ voters: Voter[] }> = ({ voters }) => (
  <Paper>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Username</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Voted</TableCell>
          <TableCell>Joined</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {voters.map(({ id, username, name, joined, voted }) => (
          <TableRow key={id}>
            <TableCell>{username}</TableCell>
            <TableCell>{name}</TableCell>
            <TableCell>{voted ? "✅" : "❌"}</TableCell>
            <TableCell>{joined ? "✅" : "❌"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
)

const fetchVotes = async (instanceId: string) => {
  try {
    const response = await api.get(`/voter/fetch-votes/${instanceId}`)
    return response.data
  } catch (error) {
    // Handle the error as needed
    console.error("Error fetching votes:", error)
    throw new Error("Failed to fetch votes")
  }
}

interface VoteCounts {
  [questionId: string]: {
    [answerId: string]: number
  }
}

interface QnAProps {
  questions: Question[]
  answers: Answer[]
  voteCounts: VoteCounts
}

const QnASection: React.FC<QnAProps> = ({ questions, answers, voteCounts }) => (
  <Paper>
    {questions
      ?.sort((a: any, b: any) => {
        const aSeconds = a.createdAt?._seconds || 0
        const bSeconds = b.createdAt?._seconds || 0
        return aSeconds - bSeconds
      })
      .map((question, index) => {
        const filteredAnswers = answers
          ?.filter((ans) => ans.questionId === question.id)
          .sort((a, b) => a.optionNum - b.optionNum)

        const data = filteredAnswers?.map((ans) => ({
          name: ans.option,
          votes: voteCounts?.[question.id]?.[ans.id] || 0,
        }))

        return (
          <Box key={question.id} mb={3} p={3}>
            <Typography variant="h6">
              Q{index + 1}: {question.description}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Type: {question.questionType}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  ticks={[0, 10, 20, 30, 40, 50]}
                  domain={[0, 50]}
                />
                <YAxis type="category" dataKey="name" />
                <Tooltip />
                <Bar dataKey="votes" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )
      })}
  </Paper>
)

const InstanceCard: React.FC<{ instanceInfo: InstanceInfo }> = ({
  instanceInfo,
}) => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const closeInstance = async () => {
    try {
      await api.put(`/admin/instances/${id}/close`)
      navigate(`/admin/event-admin/event/instance-start`)
    } catch (error) {
      console.error("Failed to close the instance:", error)
    }
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5">{instanceInfo.description}</Typography>
        <Typography>Type: {instanceInfo.votingType}</Typography>
        <Typography>Question Count: {instanceInfo.questionCount}</Typography>
        <Box m={3}>
          {instanceInfo.status === "open" ? (
            <Button variant="contained" color="primary" onClick={closeInstance}>
              Close Voting
            </Button>
          ) : (
            <AdminButton label="View Results" pathSegment="results" />
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

const fetchData = async (id: string) => {
  try {
    const response = await api.get(
      `/admin/instances/${id}/details?fetchVoters=true`
    )
    return response.data
  } catch (error) {
    // Handle the error as needed
    console.error("Error fetching instance details:", error)
    throw new Error("Failed to fetch instance details")
  }
}

const LiveInstancePage: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const auth = useAuthUser()
  const [voters, setVoters] = useState<Voter[]>([])
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [question, setQuestions] = useState<Question[]>([])

  const handleCloseSnackbar = (
    event: React.SyntheticEvent<Element, Event>,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return
    }
    setOpenSnackbar(false)
  }

  const eventId = auth()?.eventId?._path?.segments[1] ?? "null"

  // all instance ids from eventId
  useEffect(() => {
    if (eventId) {
      api
        .get(`/admin/events/${eventId}/voting-instances-ids`)
        .then((response) => {
          const instances = response.data
          const exists = instances.some((instance: any) => instance.id === id)
          if (!exists) {
            navigate(
              "/not-authorized?message=The%20voting%20instance%20you%20are%20trying%20to%20access%20does%20not%20exist."
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })

      api
        .get(`/admin/instances/${id}/status`)
        .then((response) => {
          const status = response.data.status
          if (status !== "open") {
            navigate(
              "/not-authorized?message=The%20voting%20instance%20you%20are%20trying%20to%20access%20is%20not%20open%20for%20voting."
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })
    }
  }, [eventId, id, navigate])

  // Fetching instance details
  const { data: instanceData, isLoading: instanceLoading } = useQuery(
    ["instanceDetails", id],
    () => fetchData(id),
    {
      refetchOnWindowFocus: false, // Prevent refetch on window focus
      refetchOnReconnect: false, // Prevent refetch on network reconnect
    }
  )

  useEffect(() => {
    if (instanceData?.voters) {
      setVoters(instanceData.voters)
    }
  }, [instanceData])

  // Fetching votes
  const { data: voteData, isLoading: votesLoading } = useQuery(
    ["instanceVotes", id],
    () => fetchVotes(id),
    {
      refetchOnWindowFocus: false, // Prevent refetch on window focus
      refetchOnReconnect: false, // Prevent refetch on network reconnect
    }
  )

  if (instanceLoading || votesLoading || !instanceData || !voteData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress disableShrink />
      </Box>
    )
  }

  const { instance: instanceInfo, questions, answers } = instanceData
  // let sortedQuestions
  // if (instanceData.questions && Array.isArray(instanceData.questions)) {
  //   // Sort the questions based on created at time
  //   const questionData = instanceData.questions
  //   sortedQuestions = questionData.sort((a: any, b: any) => {
  //     const aSeconds = a.createdAt?._seconds || 0
  //     const bSeconds = b.createdAt?._seconds || 0
  //     return aSeconds - bSeconds
  //   })

  //   setQuestions(sortedQuestions)
  // }

  return (
    <>
      <Box pt={8} pb={4}>
        <Grid
          container
          spacing={3}
          alignItems="stretch"
          style={{ minHeight: "80vh" }}
        >
          <Grid item xs={3}>
            <InstanceCard instanceInfo={instanceInfo} />
          </Grid>
          <Grid item xs={9}>
            <QnASection
              questions={instanceData.questions}
              answers={answers}
              voteCounts={voteData.voteCounts}
            />
            <VoterTable voters={voters} />
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity="info"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  )
}

export default LiveInstancePage
